<template>
  <div class="companies">
    <heading title="Company List" tag="company-list-title">
      <export-button
        :fields="exportFields"
        profile-key="company"
        @export="exportList"
      />
      <b-button
        v-if="canMerge"
        variant="primary"
        class="ml-3"
        :to="{ name: 'companyMerge' }"
      >
        Merge Companies
        <b-icon-chevron-bar-contract />
      </b-button>
      <b-button
        v-if="canCreate"
        variant="primary"
        class="ml-3"
        :to="{ name: 'companyCreate' }"
      >
        New Company
        <b-icon-plus-square />
      </b-button>
    </heading>
    <data-table
      ref="dataTable"
      persistName="company-filter"
      :show-filters="true"
      :show-pager="true"
      :get-items="getEntries"
      v-model="filters"
      :fields="fields"
    >
      <!-- template for custom columns -->
      <template v-slot:customField="slotProps">
        <div v-if="slotProps.row.field.key === 'actions'">
          <b-button size="sm"
                    variant="secondary"
                    class="mr-2"
                    :to="{ name: 'companyView', params: { companyId: slotProps.row.item.id } }">
            <b-icon-eye />
          </b-button>
        </div>
        <div v-else-if="slotProps.row.field.key === 'status'">
          {{ getStatusLabel(slotProps.row.item.status) }}
        </div>
        <div v-else-if="slotProps.row.field.key === 'rental'" class="text-center">
          {{ getRentalLabel(slotProps.row.item.rental) }}
        </div>
      </template>
    </data-table>
  </div>
</template>

<script>
import Vue from "vue";
import Company from "@/models/Company";
import Authorization from "@/providers/Authorization";
import Export from "@/providers/Export";
import * as _ from "lodash";

export default Vue.extend({
  name: "Companies",
  data() {
    return {
      fields: [
        { key: "id", sortable: true, label: "ID" },
        { key: "name", sortable: true, label: "Name" },
        { key: "companyProjectCount", label: "# Projects" },
        { key: "companyUserCount", label: "# Contractors" },
        { key: "equipmentCount", label: "# Equipment" },
        {
          key: "auditViolationCount",
          sortable: false,
          label: "# Audit Violations"
        },
        {
          key: "pendingExemptionCount",
          sortable: false,
          label: "Pending Exemption Requests"
        },
        { key: "cobid_types", sortable: true, label: "COBID Type" },
        { key: "status", label: "Status" },
        { key: "rental", sortable: false, label: "Rental" },
        { key: "actions" }
      ],

      exportFields: {
        name: "Company Name",
        ccb_number: "CCB License #",
        alt_license_type: "Alt Oregon License Type",
        alt_license_number: "Alt License #",
        url: "URL",
        status: "Status",
        rental: "Rental Company",
        hide_equipment: "Hide Equipment",
        equipmentCount: "# of Equipment",
        companyUserCount: "# of Users",
        companyProjectCount: "# of Projects",
        auditNoIssueCount: "# of Audits with No Issues",
        auditIssueCount: "# of Audits with Issues",
        auditViolationCount: "# of Audits with Violations",
        auditResolvedCount: "# of Resolved Audits",
        totalExemptionCount: "Total # of Exemptions",
        pendingExemptionCount: "# of Pending Exemptions",
        draftExemptionCount: "# of Drafted Exemptions",
        grantedExemptionCount: "# of Granted Exemptions",
        deniedExemptionCount: "# of Denied Exemptions",
        mailing_address: "Mailing Address",
        city: "City",
        state: "State",
        zip: "Zip",
        cobid_certified: "COBID Certified",
        cobid_number: "COBID #",
        cobid_type: "COBID type",
        contact_first_name: "Contact First Name",
        contact_last_name: "Contact Last Name",
        contact_email: "Contact Email",
        contact_phone_office: "Contact Phone (Office)",
        contact_phone_mobile: "Contact Phone (Mobile)"
      },

      filters: {
        name: {
          field: "name",
          type: "string",
          value: null,
          label: "Company Name"
        },
        address: {
          field: "address",
          label: "Address",
          type: "string"
        },
        status: {
          field: "status",
          type: "select",
          options: [
            { value: "*", text: "All" },
            { value: "ENABLED", text: "Enabled" },
            { value: "DISABLED", text: "Disabled" }
          ],
          value: null,
          label: "Status"
        },
        rental: { field: "rental", label: "Rental Company", type: "boolean" }
      },
      canCreate: false,
      canMerge: false
    };
  },
  methods: {
    getEntries(listParams) {
      const extraFields = [
        "companyProjectCount",
        "companyUserCount",
        "equipmentCount",
        "auditViolationCount",
        "pendingExemptionCount"
      ];
      return this.$api.getItems(
        this,
        "company",
        this.filters,
        listParams,
        extraFields
      );
    },
    getStatusLabel(value) {
      return Company.getStatusLabel(value);
    },
    getRentalLabel(value) {
      return value ? "✔" : "";
    },
    async exportList(type, fields) {
      //  context
      const contextTable = this.$refs.dataTable.getContext();
      const context = contextTable
        ? { sortBy: contextTable.sortBy, perPage: 100000, currentPage: 1 }
        : null;
      this.$api
        .getItems(this, "company", this.filters, context, ["companyAdminUser"])
        .then(companies => {
          //  map companyAdminUser fields
          _.each(companies, company => {
            if (company.companyAdminUser) {
              let companyAdminUser = company.companyAdminUser;
              company.contact_first_name = companyAdminUser.first_name;
              company.contact_last_name = companyAdminUser.last_name;
              company.contact_email = companyAdminUser.email;
              company.contact_phone_office = companyAdminUser.phone_office;
              company.contact_phone_mobile = companyAdminUser.phone_mobile;
            }
          });

          // callback for status label
          let callback = (key, value) => {
            switch (key) {
              case "status":
                value = this.getStatusLabel(value);
                break;
            }
            return value;
          };
          //  export

          Export.exportRecords(companies, fields, "Companies", type, callback);
        });
    }
  },
  created() {
    Authorization.can({ action: "create", model: "company" }).then(
      can => (this.canCreate = can)
    );
    Authorization.can({ action: "merge", model: "company" }).then(
      can => (this.canMerge = can)
    );
  }
});
</script>
